/**
 * "Who could absorb +X h?" — free colleagues for an over-utilized cell.
 *
 * The pool is the other Resources in the target's Discipline; only when nobody
 * else shares it does the pool widen to the target's Home Role. From the pool,
 * only cells with room that month (Some Room / Available) qualify, ranked by
 * free hours.
 *
 * Every reason is read off data in the rows — the shared Discipline or Home
 * Role, the free hours, and whether next month is also free. Nothing is
 * inferred, so a reason never claims something the grid does not show.
 *
 * 🔒 FIREWALL: HOURS only. No Fit Score, Capacity, Intensity, or points imports.
 * Candidates are ranked by free hours, never by Fit Score.
 */
import {
  type AvailabilityCutoffs,
  type CellResult,
  classifyCell,
  type FreeCellState,
  isFreeState,
} from "@/lib/availability/cell-state";
import type { AvailabilityRow } from "@/lib/availability/groups";
import { addMonths, monthLabel } from "@/lib/availability/months";

/** How many candidates to return when the caller does not say. */
export const DEFAULT_CANDIDATE_LIMIT = 3;

export interface CandidateTarget {
  resourceId: string;
  disciplineCode: string | null;
  homeRole: string | null;
}

export interface FindCandidatesInput {
  target: CandidateTarget;
  /** `"YYYY-MM-01"`. */
  month: string;
  rows: readonly AvailabilityRow[];
  cutoffs: AvailabilityCutoffs;
  limit?: number;
}

export interface Candidate {
  resourceId: string;
  name: string;
  freeHours: number;
  state: FreeCellState;
  reasons: string[];
}

function cellStateOf(
  row: AvailabilityRow,
  month: string,
  cutoffs: AvailabilityCutoffs,
): CellResult | null {
  const cell = row.cells[month];
  return cell === undefined ? null : classifyCell(cell, cutoffs);
}

export function findCandidates(input: FindCandidatesInput): Candidate[] {
  const { target, month, rows, cutoffs } = input;
  const limit = input.limit ?? DEFAULT_CANDIDATE_LIMIT;
  if (limit <= 0) return [];

  const others = rows.filter((row) => row.resourceId !== target.resourceId);
  const { disciplineCode, homeRole } = target;

  let pool: AvailabilityRow[] = [];
  let basis = "";
  if (disciplineCode !== null) {
    pool = others.filter((row) => row.disciplineCode === disciplineCode);
    basis = `Same Discipline (${disciplineCode})`;
  }
  if (pool.length === 0 && homeRole !== null) {
    pool = others.filter((row) => row.homeRole === homeRole);
    basis = `Same Home Role (${homeRole})`;
  }

  const nextMonth = addMonths(month, 1);
  const candidates: Candidate[] = [];
  for (const row of pool) {
    const result = cellStateOf(row, month, cutoffs);
    if (result === null || !isFreeState(result.state)) continue;

    const reasons = [
      basis,
      `${Math.round(result.netHours)} h free in ${monthLabel(month, { short: true })}`,
    ];
    const next = cellStateOf(row, nextMonth, cutoffs);
    if (next !== null && isFreeState(next.state)) reasons.push("Also free next month");

    candidates.push({
      resourceId: row.resourceId,
      name: row.name,
      freeHours: result.netHours,
      state: result.state,
      reasons,
    });
  }

  return candidates
    .sort((a, b) => b.freeHours - a.freeHours || a.name.localeCompare(b.name))
    .slice(0, limit);
}
